/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	ApiError,
	StartTwoFactorAuthenticationDTO,
	TwoFactorAuthenticationChangePasswordDTO,
	TwoFactorAuthenticationEnterPhoneVerificationCodeDTO,
	TwoFactorAuthenticationService
} from '../../generated/iam-client';
import {from, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
	iamApiAuthenticationChangePasswordNotFound,
	iamApiAuthenticationChangePasswordUnexpectedError,
	iamApiAuthenticationChangePasswordValidationException,
	iamApiAuthenticationEnterPhoneVerificationCodeNotFound,
	iamApiAuthenticationEnterPhoneVerificationCodeUnexpectedError,
	iamApiAuthenticationEnterPhoneVerificationCodeValidationException,
	iamApiAuthenticationPasswordChanged,
	iamApiAuthenticationPhoneVerificationCodeEntered,
	iamApiAuthenticationRestartPhoneVerificationNotFound,
	iamApiAuthenticationRestartPhoneVerificationRequested,
	iamApiAuthenticationRestartPhoneVerificationUnexpectedError,
	iamApiAuthenticationRetryPhoneVerificationNotFound,
	iamApiAuthenticationRetryPhoneVerificationRequested,
	iamApiAuthenticationRetryPhoneVerificationUnexpectedError,
	iamApiAuthenticationStarted,
	iamApiStartAuthenticationUnexpectedError,
	iamApiStartAuthenticationValidationException
} from './actions';
import {AnyAction} from 'redux';

class TwoFactorAuthenticationResource {

	startAuthentication(command: StartTwoFactorAuthenticationDTO): Observable<AnyAction> {
		const promise = TwoFactorAuthenticationService.startAuthentication(command);
		return from(promise)
			.pipe(
				map((responseBody) => iamApiAuthenticationStarted({twoFactorAuthenticationId: responseBody.id})),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 422:
							return of(iamApiStartAuthenticationValidationException(error.body));
						default:
							return of(iamApiStartAuthenticationUnexpectedError());
					}
				})
			);
	}

	retryPhoneVerification(twoFactorAuthenticationId: string): Observable<AnyAction> {
		const promise = TwoFactorAuthenticationService.retryPhoneVerification(twoFactorAuthenticationId);
		return from(promise)
			.pipe(
				map(() => iamApiAuthenticationRetryPhoneVerificationRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAuthenticationRetryPhoneVerificationNotFound());
						default:
							return of(iamApiAuthenticationRetryPhoneVerificationUnexpectedError());
					}
				})
			);
	}

	restartPhoneVerification(twoFactorAuthenticationId: string): Observable<AnyAction> {
		const promise = TwoFactorAuthenticationService.restartAuthenticationPhoneVerification(twoFactorAuthenticationId);
		return from(promise)
			.pipe(
				map(() => iamApiAuthenticationRestartPhoneVerificationRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAuthenticationRestartPhoneVerificationNotFound());
						default:
							return of(iamApiAuthenticationRestartPhoneVerificationUnexpectedError());
					}
				})
			);
	}

	enterPhoneVerificationCode(
		twoFactorAuthenticationId: string, command: TwoFactorAuthenticationEnterPhoneVerificationCodeDTO
	): Observable<AnyAction> {
		const promise = TwoFactorAuthenticationService.enterPhoneVerificationCode(twoFactorAuthenticationId, command);
		return from(promise)
			.pipe(
				map(() => iamApiAuthenticationPhoneVerificationCodeEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAuthenticationEnterPhoneVerificationCodeNotFound());
						case 422:
							return of(iamApiAuthenticationEnterPhoneVerificationCodeValidationException(error.body));
						default:
							return of(iamApiAuthenticationEnterPhoneVerificationCodeUnexpectedError());
					}
				})
			);
	}

	changePassword(twoFactorAuthenticationId: string, command: TwoFactorAuthenticationChangePasswordDTO): Observable<AnyAction> {
		const promise = TwoFactorAuthenticationService.changeAuthenticationPassword(twoFactorAuthenticationId, command);
		return from(promise)
			.pipe(
				map(() => iamApiAuthenticationPasswordChanged()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiAuthenticationChangePasswordNotFound());
						case 422:
							return of(iamApiAuthenticationChangePasswordValidationException(error.body));
						default:
							return of(iamApiAuthenticationChangePasswordUnexpectedError());
					}
				})
			);
	}
}

export default TwoFactorAuthenticationResource;
